import { useEffect, useState } from 'react';
import type { LatLng } from './types';

/**
 * When a ride option would drop the rider: now + the nearest driver's pickup estimate + the trip's own duration
 * (both from the server). Null when no driver of that type is near (no honest time to show).
 */
export function dropAt(now: number, etaMinutes: number | null | undefined, durationSeconds: number): Date | null {
  if (etaMinutes == null || !Number.isFinite(etaMinutes)) return null;
  return new Date(now + etaMinutes * 60_000 + durationSeconds * 1000);
}

/** Compass bearing from a to b in degrees (0 = north, 90 = east). */
export function bearing(a: LatLng, b: LatLng): number {
  const r = Math.PI / 180;
  const y = Math.sin((b.lng - a.lng) * r) * Math.cos(b.lat * r);
  const x = Math.cos(a.lat * r) * Math.sin(b.lat * r) - Math.sin(a.lat * r) * Math.cos(b.lat * r) * Math.cos((b.lng - a.lng) * r);
  return ((Math.atan2(y, x) / r) % 360 + 360) % 360;
}

/** The way from `from` to `to` degrees the short way round (−180…180), so a turn through north doesn't spin. */
export const turn = (from: number, to: number) => ((((to - from) % 360) + 540) % 360) - 180;

/** The clock, ticking every `ms` (drop-off times and ETAs stay right while the screen is open). */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(t);
  }, [ms]);
  return now;
}
